import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, X, Crown, Check, MapPin, Layers, Users } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { billingApi, formatError } from "../lib/api";

export const PremiumModal = ({ open, onClose }) => {
  const [busy, setBusy] = useState(false);
  // billing_mode: "live" → real Stripe charges; anything else → button is
  // hidden behind a "Coming soon" placeholder. This protects production
  // users from accidentally "buying" Premium on the sandbox key.
  const [billingMode, setBillingMode] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await billingApi.packages();
        if (!cancelled) setBillingMode(data?.billing_mode || "disabled");
      } catch (err) {
        if (!cancelled) setBillingMode("disabled");
        console.warn("Failed to load billing mode:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      // Pass the frontend origin so the backend can construct success_url
      // and cancel_url that work on whichever domain we're currently on
      // (preview or production) without any hardcoding.
      const { url } = await billingApi.createCheckoutSession(
        "premium_monthly",
        window.location.origin,
      );
      if (!url) throw new Error("No checkout URL returned");
      // Full-page redirect into Stripe-hosted Checkout. They'll bounce
      // back to /billing/success?session_id=... where we poll for the
      // payment result and flip is_premium.
      window.location.href = url;
    } catch (err) {
      toast.error(formatError(err));
      setBusy(false);
    }
  };

  const benefits = [
    { id: "containers", icon: Layers, text: "Unlimited active containers (free is capped at 3)" },
    { id: "locations", icon: MapPin, text: "Unlimited storage locations: fridge, pantry, garage…" },
    { id: "households", icon: Users, text: "Switch between multiple households on the same account" },
    { id: "ai-nutrition", icon: Check, text: "AI nutrition photo analysis (coming soon)" },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-md flex items-end sm:items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="premium-modal"
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-7 sm:m-4 max-h-[88vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-2xl brand-gradient-bg flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <button onClick={onClose} className="text-[#8A93A6] p-1" data-testid="premium-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#277A82]">
              QFresh Premium
            </div>
            <h2 className="font-heading text-3xl font-bold brand-gradient-text mt-1">
              Track everything
            </h2>
            <p className="text-[#4F5D75] mt-2 leading-relaxed">
              Free tier covers 3 active containers in one household. Upgrade to unlock the whole kitchen.
            </p>

            <ul className="mt-5 space-y-3" data-testid="premium-benefits">
              {benefits.map((b) => (
                <li key={b.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#DCEDF0] flex items-center justify-center shrink-0 mt-0.5">
                    <b.icon className="w-4 h-4 text-[#1F5566]" />
                  </div>
                  <span className="text-sm text-[#1F5566] leading-relaxed pt-1.5">{b.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 tup-card p-5 border-2 border-[#277A82] relative">
              <div className="absolute -top-2 left-4 px-2 py-0.5 brand-gradient-bg text-white text-[10px] font-bold rounded-full">
                MOST POPULAR
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-heading text-4xl font-bold text-[#1F5566]">$9.99</div>
                <div className="text-sm text-[#8A93A6]">/ month</div>
              </div>
              <div className="text-xs text-[#8A93A6] mt-1">Cancel anytime. No hidden fees.</div>
            </div>

            {billingMode === "live" ? (
              <>
                <Button
                  onClick={startCheckout}
                  disabled={busy}
                  data-testid="premium-cta-btn"
                  className="w-full mt-5 brand-gradient-bg text-white h-12 hover:opacity-90"
                >
                  {busy ? "Redirecting to Stripe…" : "Upgrade — $9.99/mo"}
                </Button>
                <p className="text-[10px] text-center text-[#8A93A6] mt-3">
                  Secure checkout by Stripe. Cancel any time — your premium runs
                  for 30 days from each successful payment.
                </p>
              </>
            ) : (
              <>
                <div
                  data-testid="premium-coming-soon"
                  className="w-full mt-5 h-12 rounded-md border-2 border-dashed border-[#C9C4B5] bg-[#F5F3ED] flex items-center justify-center gap-2 text-[#8A93A6] font-semibold"
                >
                  <Lock className="w-4 h-4" /> Coming soon
                </div>
                <p className="text-[10px] text-center text-[#8A93A6] mt-3">
                  We're finalising payments. The Upgrade button will turn on
                  shortly — thanks for your patience!
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const PremiumLockTile = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -3 }}
        onClick={() => setOpen(true)}
        data-testid="premium-lock-tile"
        className="relative tup-card p-3 text-left border-dashed border-[#C9C4B5] bg-gradient-to-br from-white to-[#F5F3ED] overflow-hidden"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-bold px-2 py-0.5 rounded-full brand-gradient-bg text-white">
            PREMIUM
          </div>
          <div className="w-7 h-7 rounded-full brand-gradient-bg flex items-center justify-center">
            <Lock className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
        <h3 className="font-heading text-base font-semibold text-[#1F5566] mt-3">
          Unlimited slots
        </h3>
        <p className="text-[10px] text-[#4F5D75] mt-1 leading-snug">
          Free plan caps at 3 active containers. Tap to upgrade.
        </p>
        <div className="mt-2 text-[10px] font-bold text-[#277A82]">$9.99/mo →</div>
      </motion.button>
      <PremiumModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};
